import { nanoid } from "nanoid";

import { DEFAULT_WRITTEN_COMPETENCY } from "./edit-company-modal.constants";
import {
  CompanyQuestionTemplateAssessmentResponseFormat,
  CompanyQuestionTemplateBasicQuestionInputType,
  type CompanyQuestionTemplate,
  type CompanyQuestionTemplateBasicQuestion,
  type UpsertCompanyQuestionTemplateOption,
  type UpsertCompanyQuestionTemplatePayload,
} from "@/types/company-manage";
import {
  AssessmentQuestionResponseFormat,
  type Condition,
  QuestionType,
  type ReviewPageBasicQuestion,
  type ReviewPageCompetency,
  type ReviewPageDraftQuestion,
  type ReviewPageQuestion,
  type ReviewPageScoringGuidelines,
} from "@/types/screener-builder";
import {
  getChoiceRules,
  getNumericalRule,
  getYesNoRule,
  hasValidationRules,
} from "@/utils/questions";
import { isEmptyAfterTrim, trimStringValues, trimToNull } from "@/utils/string";

const sortByOrder = <T extends { questionOrder?: number }>(questions: T[]) =>
  [...questions].sort(
    (a, b) => (a.questionOrder ?? 0) - (b.questionOrder ?? 0),
  );

const sortByOptionOrder = <T extends { optionOrder?: number }>(options: T[]) =>
  [...options].sort((a, b) => (a.optionOrder ?? 0) - (b.optionOrder ?? 0));

const makeInternalLabel = (questionText: string, index: number) => {
  const compactQuestion = questionText
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);

  return compactQuestion || `default_question_${index + 1}`;
};

const makeIndexedInternalLabel = (questionText: string, index: number) =>
  `${makeInternalLabel(questionText, index).slice(0, 45)}_${index + 1}`;

const EMPTY_SCORING_GUIDELINES = {
  high: "",
  low: "",
  medium: "",
} satisfies ReviewPageScoringGuidelines;

export const questionTypeOptions = () => [
  {
    label: "Single /Multiple Choice",
    value: QuestionType.SingleChoice,
  },
  {
    label: "Short Answer",
    value: QuestionType.ShortAnswer,
  },
  {
    label: "File upload",
    value: QuestionType.FileUpload,
  },
  {
    label: "Number",
    value: QuestionType.Number,
  },
  {
    label: "Yes/No",
    value: QuestionType.YesNo,
  },
];

const toQuestionType = (
  inputType?: CompanyQuestionTemplateBasicQuestionInputType,
) => {
  switch (inputType) {
    case CompanyQuestionTemplateBasicQuestionInputType.Email:
      return QuestionType.Email;
    case CompanyQuestionTemplateBasicQuestionInputType.FileUpload:
      return QuestionType.FileUpload;
    case CompanyQuestionTemplateBasicQuestionInputType.MultipleChoice:
      return QuestionType.MultipleChoice;
    case CompanyQuestionTemplateBasicQuestionInputType.Name:
      return QuestionType.Name;
    case CompanyQuestionTemplateBasicQuestionInputType.Numerical:
      return QuestionType.Number;
    case CompanyQuestionTemplateBasicQuestionInputType.Phone:
      return QuestionType.Phone;
    case CompanyQuestionTemplateBasicQuestionInputType.SingleChoice:
      return QuestionType.SingleChoice;
    case CompanyQuestionTemplateBasicQuestionInputType.Voice:
      return QuestionType.Voice;
    case CompanyQuestionTemplateBasicQuestionInputType.YesNo:
      return QuestionType.YesNo;
    case CompanyQuestionTemplateBasicQuestionInputType.LongText:
    case CompanyQuestionTemplateBasicQuestionInputType.Text:
    default:
      return QuestionType.ShortAnswer;
  }
};

const toTemplateInputType = (questionType: QuestionType) => {
  switch (questionType) {
    case QuestionType.Email:
      return CompanyQuestionTemplateBasicQuestionInputType.Email;
    case QuestionType.FileUpload:
      return CompanyQuestionTemplateBasicQuestionInputType.FileUpload;
    case QuestionType.MultipleChoice:
      return CompanyQuestionTemplateBasicQuestionInputType.MultipleChoice;
    case QuestionType.Name:
      return CompanyQuestionTemplateBasicQuestionInputType.Name;
    case QuestionType.Number:
      return CompanyQuestionTemplateBasicQuestionInputType.Numerical;
    case QuestionType.Phone:
      return CompanyQuestionTemplateBasicQuestionInputType.Phone;
    case QuestionType.SingleChoice:
      return CompanyQuestionTemplateBasicQuestionInputType.SingleChoice;
    case QuestionType.Voice:
      return CompanyQuestionTemplateBasicQuestionInputType.Voice;
    case QuestionType.YesNo:
      return CompanyQuestionTemplateBasicQuestionInputType.YesNo;
    case QuestionType.ShortAnswer:
    default:
      return CompanyQuestionTemplateBasicQuestionInputType.Text;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const toReviewValidationRules = (
  question: CompanyQuestionTemplateBasicQuestion,
): ReviewPageBasicQuestion["validationRules"] => {
  const qualifyingExpression = question.qualifyingExpression;

  if (
    isRecord(qualifyingExpression) &&
    isRecord(qualifyingExpression.validationRules)
  ) {
    return qualifyingExpression.validationRules as ReviewPageBasicQuestion["validationRules"];
  }

  if (
    question.options?.length &&
    [
      CompanyQuestionTemplateBasicQuestionInputType.MultipleChoice,
      CompanyQuestionTemplateBasicQuestionInputType.SingleChoice,
    ].includes(
      question.inputType as CompanyQuestionTemplateBasicQuestionInputType,
    )
  ) {
    return {
      choiceRules: sortByOptionOrder(question.options).map((option) => ({
        optionText: option.optionText,
        qualifier:
          isRecord(option.qualifyingExpression) &&
          typeof option.qualifyingExpression.qualifyingExpression === "string"
            ? (option.qualifyingExpression
                .qualifyingExpression as Condition["qualifier"])
            : undefined,
      })),
    };
  }

  const expressionValue =
    typeof qualifyingExpression === "string"
      ? qualifyingExpression
      : isRecord(qualifyingExpression) &&
          typeof qualifyingExpression.qualifyingExpression === "string"
        ? qualifyingExpression.qualifyingExpression
        : null;

  if (
    question.inputType === CompanyQuestionTemplateBasicQuestionInputType.YesNo
  ) {
    const match = expressionValue?.match(/\[="(yes|no)"\]/);

    if (match) {
      return {
        yesNoRule: match[1],
      } as ReviewPageBasicQuestion["validationRules"];
    }
  }

  if (
    question.inputType ===
    CompanyQuestionTemplateBasicQuestionInputType.Numerical
  ) {
    const match = expressionValue?.match(/\[(.*)\]/);

    if (match) {
      return { numericalRule: match[1] };
    }
  }

  return null;
};

export const toReviewBasicQuestions = (
  template?: CompanyQuestionTemplate | null,
): ReviewPageBasicQuestion[] =>
  sortByOrder(template?.basicQuestions ?? [])
    .filter((question) => !isEmptyAfterTrim(question.questionText))
    .map((question, index) => {
      const questionText = question.questionText?.trim() ?? "";
      const questionType = toQuestionType(question.inputType);

      return {
        id: question.id ?? nanoid(),
        internalLabel:
          question.internalLabel ?? makeInternalLabel(questionText, index),
        isCustom: true,
        isIncluded: question.isIncluded ?? true,
        isQualifying: question.isQualifying ?? false,
        questionText,
        questionType,
        rootId: question.id,
        showInSummary: question.showInSummary ?? false,
        validationRules: toReviewValidationRules(question),
      };
    });

const toAssessmentResponseFormat = (
  responseFormat?: CompanyQuestionTemplateAssessmentResponseFormat,
) => {
  switch (responseFormat) {
    case CompanyQuestionTemplateAssessmentResponseFormat.VoiceRecommended:
      return AssessmentQuestionResponseFormat.VoiceRecommended;
    case CompanyQuestionTemplateAssessmentResponseFormat.VoiceRequired:
      return AssessmentQuestionResponseFormat.VoiceRequired;
    case CompanyQuestionTemplateAssessmentResponseFormat.Written:
    default:
      return AssessmentQuestionResponseFormat.Written;
  }
};

const toTemplateAssessmentResponseFormat = (
  responseFormat?: AssessmentQuestionResponseFormat,
) => {
  switch (responseFormat) {
    case AssessmentQuestionResponseFormat.VoiceRecommended:
      return CompanyQuestionTemplateAssessmentResponseFormat.VoiceRecommended;
    case AssessmentQuestionResponseFormat.VoiceRequired:
      return CompanyQuestionTemplateAssessmentResponseFormat.VoiceRequired;
    case AssessmentQuestionResponseFormat.Written:
    default:
      return CompanyQuestionTemplateAssessmentResponseFormat.Written;
  }
};

const toReviewScoringGuidelines = (
  value: unknown,
): ReviewPageScoringGuidelines => {
  if (value === null || typeof value !== "object") {
    return { ...EMPTY_SCORING_GUIDELINES };
  }

  const maybeGuidelines = value as Partial<ReviewPageScoringGuidelines>;

  return {
    high: typeof maybeGuidelines.high === "string" ? maybeGuidelines.high : "",
    low: typeof maybeGuidelines.low === "string" ? maybeGuidelines.low : "",
    medium:
      typeof maybeGuidelines.medium === "string" ? maybeGuidelines.medium : "",
  };
};

export const toReviewCompetencies = (
  template?: CompanyQuestionTemplate | null,
): ReviewPageCompetency[] =>
  [...(template?.competencies ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .reduce<ReviewPageCompetency[]>((reviewCompetencies, competency) => {
      const templateQuestions = sortByOrder(
        competency.assessmentQuestions ?? [],
      ).filter((question) => !isEmptyAfterTrim(question.questionText));

      if (templateQuestions.length === 0) {
        return reviewCompetencies;
      }

      const hasSelectedQuestion = templateQuestions.some(
        (question) => question.isSelected === true,
      );
      const questions: ReviewPageQuestion[] = templateQuestions.map(
        (question, questionIndex) => ({
          canSkip: question.canSkip ?? false,
          isSelected:
            question.isSelected === true ||
            (!hasSelectedQuestion && questionIndex === 0),
          question: question.questionText.trim(),
          questionId: question.id ?? nanoid(),
          recordingDurationSeconds: question.recordingDurationSeconds ?? 60,
          responseFormat: toAssessmentResponseFormat(question.responseFormat),
        }),
      );
      const selectedQuestionText =
        questions.find((question) => question.isSelected)?.question ??
        questions[0].question;

      reviewCompetencies.push({
        custom: competency.isCustom ?? true,
        definition:
          competency.definition || DEFAULT_WRITTEN_COMPETENCY.definition,
        displayName:
          competency.displayName ||
          competency.name ||
          DEFAULT_WRITTEN_COMPETENCY.displayName,
        hasCustomScoringGuidelines:
          competency.hasCustomScoringGuidelines ?? false,
        id: competency.id ?? nanoid(),
        isIncluded: competency.isIncluded ?? true,
        name:
          competency.name ||
          makeIndexedInternalLabel(
            selectedQuestionText,
            reviewCompetencies.length,
          ),
        questions,
        scoringGuidelines: toReviewScoringGuidelines(
          competency.scoringGuidelines,
        ),
        warningBypassLog: (competency.warningBypassLog ??
          null) as ReviewPageCompetency["warningBypassLog"],
      });

      return reviewCompetencies;
    }, []);

const toTemplateQuestionOptions = (
  question: ReviewPageDraftQuestion,
): UpsertCompanyQuestionTemplateOption[] =>
  (getChoiceRules(question) ?? []).map((rule) => ({
    optionText: rule.optionText,
    optionValue: rule.optionText,
    qualifyingExpression: rule.qualifier
      ? { qualifyingExpression: rule.qualifier }
      : null,
  }));

const toTemplateQualifyingExpression = (question: ReviewPageDraftQuestion) => {
  if (!question.isQualifying || !hasValidationRules(question)) {
    return null;
  }

  const yesNoRule = getYesNoRule(question);
  if (yesNoRule) {
    return { qualifyingExpression: `[="${yesNoRule}"]` };
  }

  const numericalRule = getNumericalRule(question);
  if (numericalRule) {
    return { qualifyingExpression: `[${numericalRule}]` };
  }

  return null;
};

const hasNonEmptyScoringGuidelines = (
  scoringGuidelines: ReviewPageScoringGuidelines,
) =>
  !isEmptyAfterTrim(scoringGuidelines.high) ||
  !isEmptyAfterTrim(scoringGuidelines.low) ||
  !isEmptyAfterTrim(scoringGuidelines.medium);

export const toTemplatePayload = ({
  basicQuestions,
  closingStatement,
  openingStatement,
  writtenQuestions,
}: {
  basicQuestions: ReviewPageBasicQuestion[];
  closingStatement: string;
  openingStatement: string;
  writtenQuestions: ReviewPageCompetency[];
}): UpsertCompanyQuestionTemplatePayload => {
  const cleanedWrittenQuestions = writtenQuestions
    .map((competency) => ({
      competency,
      questions: competency.questions.filter(
        (question) => !isEmptyAfterTrim(question.question),
      ),
    }))
    .filter(
      (
        entry,
      ): entry is {
        competency: ReviewPageCompetency;
        questions: ReviewPageQuestion[];
      } => entry.questions.length > 0,
    );

  return {
    basicQuestions: basicQuestions
      .map((question) => trimStringValues(question))
      .filter((question) => !isEmptyAfterTrim(question.questionText))
      .map((question, index) => ({
        inputType: toTemplateInputType(question.questionType),
        internalLabel:
          question.internalLabel ||
          makeInternalLabel(question.questionText, index),
        isIncluded: question.isIncluded,
        isQualifying: question.isQualifying,
        maxFollowups: 0,
        options: toTemplateQuestionOptions(question),
        qualifyingExpression: toTemplateQualifyingExpression(question),
        questionText: question.questionText,
        showInSummary: question.showInSummary ?? false,
      })),
    closingStatement: trimToNull(closingStatement),
    competencies: cleanedWrittenQuestions.map(
      ({ competency, questions }, index) => ({
        assessmentQuestions: questions.map((question) => ({
          canSkip: question.canSkip ?? false,
          isCustom: competency.custom ?? true,
          isSelected: question.isSelected,
          maxFollowups: 2,
          questionText: question.question.trim(),
          recordingDurationSeconds: question.recordingDurationSeconds ?? 60,
          responseFormat: toTemplateAssessmentResponseFormat(
            question.responseFormat,
          ),
        })),
        definition:
          competency.definition || DEFAULT_WRITTEN_COMPETENCY.definition,
        displayName:
          competency.displayName ||
          competency.name ||
          DEFAULT_WRITTEN_COMPETENCY.displayName,
        hasCustomScoringGuidelines: competency.hasCustomScoringGuidelines,
        isCustom: competency.custom ?? DEFAULT_WRITTEN_COMPETENCY.isCustom,
        isIncluded: competency.isIncluded,
        name:
          competency.name ||
          makeIndexedInternalLabel(questions[0].question.trim(), index),
        scoringGuidelines: hasNonEmptyScoringGuidelines(
          competency.scoringGuidelines,
        )
          ? competency.scoringGuidelines
          : null,
        warningBypassLog: competency.warningBypassLog ?? null,
      }),
    ),
    openingStatement: trimToNull(openingStatement),
  };
};
